import { SecureLinkParams } from '@/types';

/**
 * Filter the query to find the values we need
 * @param query Location Search Value (eg. ?e=asd&conversation=12ad0&topic=payment)
 * @returns filtered query values
 */
export function filterQueryParam(query: string): SecureLinkParams {
  const queryCodes = query.replace(/\?/, '').split('&');
  const hasViewCode = queryCodes.filter((viewCode) => viewCode.startsWith('v'));
  const hasErrorCode = queryCodes.filter((errorCode) =>
    errorCode.startsWith('ec')
  );
  const secureLinkCode = queryCodes.filter((linkCode) =>
    linkCode.startsWith('lc')
  );
  const deviceProofKey = queryCodes.filter((proofKeyCode) =>
    proofKeyCode.startsWith('c')
  );
  const domain = queryCodes.filter((domainCode) => domainCode.startsWith('d'));
  return {
    viewCode: hasViewCode.length ? hasViewCode[0].slice(2) : null, // ?v=
    errorCode: hasErrorCode.length ? hasErrorCode[0].slice(3) : null, //?ec=
    linkCode: secureLinkCode.length ? secureLinkCode[0].slice(3) : null, //&lc=
    deviceProofKey: deviceProofKey.length ? deviceProofKey[0].slice(2) : null, //?c=
    domain: domain.length ? domain[0].slice(2) : null, //?d=
  };
}

const MERCHANT_CUSTOMERS_PATH_MAX_LENGTH = 256;
const MERCHANT_ID_MAX_LENGTH = 128;

function isHexOrHyphenChar(char: string): boolean {
  const code: number = char.charCodeAt(0);

  return (
    (code >= 48 && code <= 57)
    || (code >= 97 && code <= 102)
    || code === 45
  );
}

function isSafeMerchantIdChar(char: string): boolean {
  const code: number = char.charCodeAt(0);

  return (
    (code >= 48 && code <= 57)
    || (code >= 65 && code <= 90)
    || (code >= 97 && code <= 122)
    || char === '-'
    || char === '_'
  );
}

/**
 * Allowlists merchantId for URL path segments; returns a new string or null.
 * Blocks slashes and path traversal while preserving normal avid values.
 */
export function sanitizeMerchantId(raw: string | null | undefined): string | null {
  if (!raw || raw.length > MERCHANT_ID_MAX_LENGTH) {
    return null;
  }

  if (raw.includes('..') || raw.includes('/') || raw.includes('\\') || raw.includes(':')) {
    return null;
  }

  let sanitized: string = '';

  for (const char of raw) {
    if (!isSafeMerchantIdChar(char)) {
      return null;
    }

    sanitized += char;
  }

  return sanitized;
}

/**
 * Returns true when merchantId contains only lowercase hex chars and hyphens.
 */
export function isValidMerchantId(merchantId: string): boolean {
  if (!merchantId || merchantId.length > MERCHANT_ID_MAX_LENGTH) {
    return false;
  }

  for (const char of merchantId) {
    if (!isHexOrHyphenChar(char)) {
      return false;
    }
  }

  return true;
}

/**
 * Returns true when pathname matches /merchants/{merchantId}/customers.
 * merchantId must contain only lowercase hex chars and hyphens.
 * Uses string parsing instead of regex to avoid ReDoS on user-controlled paths.
 */
export function isMerchantCustomersPath(pathname: string): boolean {
  if (!pathname || pathname.length > MERCHANT_CUSTOMERS_PATH_MAX_LENGTH) {
    return false;
  }

  const segments: string[] = pathname.split('/').filter(Boolean);

  if (segments.length !== 3) {
    return false;
  }

  const [root, merchantId, tail] = segments;

  if (root !== 'merchants' || tail !== 'customers') {
    return false;
  }

  if (!isValidMerchantId(merchantId)) {
    return false;
  }

  // Exact match — same as former /^\/merchants\/[a-f0-9-]+\/customers$/ (no trailing slash, no //)
  return pathname === `/merchants/${merchantId}/customers`;
}
